import { useState } from "react";
import Grid from "@mui/material/Grid";
import { Typography, Box } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import MzButton from "@markaaz/ui/MzButton";
import MzInput from "@markaaz/ui/MzInputControls";
import MzCheckbox from "@markaaz/ui/MzCheckbox";
import { MzDialog, MzDialogTitle, MzDialogContent, MzDialogActions } from "@markaaz/ui/MzDialogControls";
import MzSelect from "@markaaz/ui/MzSelect";
import { MzMenuItem } from "@markaaz/ui/MzMenuControls";
import dayjs from "dayjs";
import restrictedEmails from "../../utils/restrictedEmails";

const sx = {
  disabled: {
    "& input[disabled]": {
      backgroundColor: (theme) => theme.palette.darkBlue.c5
    }
  }
};

const commonSchema = yup.object({
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  email: yup
    .string()
    .email("Enter a valid email id")
    .required("Email is required")
    .test(
      "email-domain",
      "Please enter a business email address",
      (value) => !restrictedEmails.some((e) => value.includes(`@${e}`))
    ),
  role: yup.string().required("Role is required")
});

const adminSchema = yup.object({
  userId: yup.string().required("User ID is required"),
  phoneNumber: yup
    .string()
    .when(["isSmsOptIn"], (isSmsOptIn, schema) => {
      const shouldRequirePhoneNumber = isSmsOptIn.includes(true);
      return shouldRequirePhoneNumber ? schema.required("Mobile number required for SMS notifications.") : schema;
    })
    .nullable()
    .notRequired()
});

export default function UserForm({
  open,
  companyName,
  selectedPartner,
  selectedUser,
  roles,
  isAdmin,
  onClose,
  onSubmit
}) {
  let validationSchema = isAdmin && selectedUser ? commonSchema.concat(adminSchema) : commonSchema;
  const [isFormValid, setFormValid] = useState(false);
  const disableEmail = selectedUser?.status === "Active";

  const initialValues = {
    firstName: selectedUser?.firstName || "",
    lastName: selectedUser?.lastName || "",
    email: selectedUser?.email || "",
    role: selectedUser?.userRoleId || "",
    jobTitle: selectedUser?.jobTitle || "",
    phoneNumber: selectedUser?.phoneNumber || "",
    userId: selectedUser?.userId || "",
    isEmailOptIn: selectedUser?.isEmailEnabled || false,
    isSmsOptIn: selectedUser?.isSMSEnabled || false
  };

  function validateForm(values) {
    validationSchema
      .validate(values)
      .then(() => {
        setFormValid(true);
      })
      .catch(() => {
        setFormValid(false);
      });
  }

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
      await onSubmit(formik.values);
      formik.resetForm({ values: initialValues });
      setFormValid(false);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validate: validateForm
  });

  function handleClose() {
    formik.resetForm({ values: initialValues });
    setFormValid(false);
    onClose();
  }

  return (
    <MzDialog open={open}>
      {!selectedUser && <MzDialogTitle>Invite New User</MzDialogTitle>}
      {selectedUser && <MzDialogTitle>Edit User</MzDialogTitle>}

      <MzDialogContent>
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <MzInput
              name="firstName"
              label="First Name"
              autoComplete="off"
              required
              fullWidth
              value={formik.values.firstName}
              error={formik.touched.firstName && !!formik.errors.firstName}
              helperText={formik.touched.firstName && formik.errors.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="lastName"
              label="Last Name"
              autoComplete="off"
              required
              fullWidth
              value={formik.values.lastName}
              error={formik.touched.lastName && !!formik.errors.lastName}
              helperText={formik.touched.lastName && formik.errors.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              sx={sx.disabled}
              name="email"
              label="User Email"
              autoComplete="off"
              required
              disabled={disableEmail}
              fullWidth
              value={formik.values.email}
              error={formik.touched.email && !!formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzSelect
              label="User Role"
              name="role"
              value={formik.values.role}
              fullWidth={true}
              required
              error={formik.touched.role && !!formik.errors.role}
              helperText={formik.touched.role && formik.errors.role}
              onChange={formik.handleChange}
            >
              {roles.map((role) => {
                return (
                  <MzMenuItem key={role.userRoleId} value={role.userRoleId}>
                    {role.userRoleName}
                  </MzMenuItem>
                );
              })}
            </MzSelect>
          </Grid>
          <Grid item xs={6}>
            <MzInput sx={sx.disabled} name="company" label="Company" disabled={true} fullWidth value={companyName} />
          </Grid>
          {selectedUser && (
            <>
              <Grid item xs={6}>
                <MzInput
                  name="jobTitle"
                  label="Job Title"
                  fullWidth
                  value={formik.values.jobTitle}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Grid>
              <Grid item xs={6}>
                <MzInput
                  sx={sx.disabled}
                  name="userId"
                  label="User ID"
                  disabled={true}
                  fullWidth
                  value={formik.values.userId}
                  error={formik.touched.userId && !!formik.errors.userId}
                  helperText={formik.touched.userId && formik.errors.userId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Grid>
              <Grid item xs={6}>
                <MzInput
                  name="phoneNumber"
                  label="Phone Number"
                  fullWidth
                  value={formik.values.phoneNumber}
                  error={formik.touched.phoneNumber && !!formik.errors.phoneNumber}
                  helperText={
                    (formik.touched.phoneNumber && formik.errors.phoneNumber) ||
                    " Mobile number required for SMS notifications."
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2M" color="darkBlue.c100">
                  Notifications Opt-In
                  <Typography variant="body2M" color="negative.c100">
                    *
                  </Typography>
                </Typography>
                <Box>
                  <MzCheckbox
                    name="isEmailOptIn"
                    label="Email"
                    checked={formik.values.isEmailOptIn}
                    onChange={formik.handleChange}
                  ></MzCheckbox>
                  {selectedUser?.emailOptInDate && (
                    <Typography variant="caption" color="darkBlue.c70">
                      Updated: {dayjs(selectedUser?.emailOptInDate).format("MM/DD/YY")}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <MzCheckbox
                    name="isSmsOptIn"
                    label="SMS"
                    checked={formik.values.isSmsOptIn}
                    onChange={(event) => {
                      formik.handleChange(event);
                      formik.setFieldTouched("phoneNumber", true, false);
                    }}
                  ></MzCheckbox>
                  {selectedUser?.smsOptInDate && (
                    <Typography variant="caption" color="darkBlue.c70">
                      &nbsp;&nbsp; Updated: {dayjs(selectedUser?.smsOptInDate).format("MM/DD/YY")}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </MzDialogContent>
      <MzDialogActions
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <MzButton variant="outlined" color="secondary" onClick={handleClose}>
          Cancel
        </MzButton>
        <MzButton variant="contained" onClick={formik.handleSubmit} disabled={!isFormValid}>
          {selectedUser ? "Okay" : "Send Invitation"}
        </MzButton>
      </MzDialogActions>
    </MzDialog>
  );
}
